import { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import type { TransitionType, TimelineShot } from '@/types/timeline.ts';
import { ShotBar } from './ShotBar.tsx';
import { Playhead } from './Playhead.tsx';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import styles from './ShotLane.module.css';
import {
  type SceneTimeSpan,
  sceneAtTime,
  timeToScrollLeft,
  scrollLeftToTime,
  pickScrollTarget,
  minimalScrollOffsetToReveal,
} from '@/components/SceneBoard/sceneBoardScroll';
import {
  selectionOwnsEntryScroll,
  isRangeFullyVisible,
} from '@/components/shotLaneEntryScroll';

interface PopoverState {
  fromShotId: number;
  toShotId: number;
  x: number;
  y: number;
}

export function ShotLane() {
  const { t } = useTranslation('timeline');
  const project = useProjectStore(s => s.project);
  const pixelsPerSecond = useUiStore(s => s.pixelsPerSecond);
  const currentTime = usePlaybackStore(s => s.currentTime);
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const seek = usePlaybackStore(s => s.seek);
  const scrollLeft = useUiStore(s => s.scrollLeft);
  const setScrollLeft = useUiStore(s => s.setScrollLeft);
  const getTransition = useProjectStore(s => s.getTransition);
  const setTransition = useProjectStore(s => s.setTransition);
  const readOnly = useProjectStore(s => s.readOnly);

  const [popover, setPopover] = useState<PopoverState | null>(null);
  const openAiWorkspace = useUiStore(s => s.openAiWorkspace);

  const selectedShotId = useProjectStore(s => s.selectedShotId);
  const getShotStartTime = useProjectStore(s => s.getShotStartTime);

  const laneRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const isScrollSource = useRef(false);
  const initialScrollDone = useRef(false);

  const getActiveScenes = useProjectStore(s => s.getActiveScenes);
  const getViewOffset = useProjectStore(s => s.getViewOffset);
  const getViewDuration = useProjectStore(s => s.getViewDuration);
  const activeSceneId = useProjectStore(s => s.activeSceneId); // re-render trigger for scene changes
  const reorderShotInScene = useProjectStore(s => s.reorderShotInScene);

  // ── Board ⇄ Timeline anchor sync ──────────────────────────────────────────
  const timelineAnchor = useUiStore(s => s.timelineAnchor);
  const sceneBoardAnchorSceneId = useUiStore(s => s.sceneBoardAnchorSceneId);
  // setTimelineAnchor / setSceneBoardAnchor are read from getState() in the unmount
  // cleanup to avoid stale closures — no render-level subscription needed.

  // ── Reorder drag state ──────────────────────────────────────────────
  const reorderRef = useRef<{
    shotId: number;
    sceneId: number;
    startX: number;
    activated: boolean;
    pointerId: number;
  } | null>(null);
  const [reorderDropIndex, setReorderDropIndex] = useState<number | null>(null);
  const [reorderDropX, setReorderDropX] = useState(0);
  const reorderAutoScrollRef = useRef<number | null>(null);
  const reorderPointerXRef = useRef(0);

  const isMobile = useMobileLayout();

  // Track container width for viewport windowing (avoids rendering 500+ off-screen shots)
  const containerWidthRef = useRef(1200);
  useEffect(() => {
    const el = laneRef.current;
    if (!el) return;
    containerWidthRef.current = el.clientWidth;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) containerWidthRef.current = entry.contentRect.width;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Board ⇄ Timeline: apply saved anchor on mount (before paint) ──────────
  // Empty deps: intentional — runs once on mount, reads initial closure values.
  // Fires before the selected-shot centering useEffect (layout effects precede
  // passive effects). When 'reanchor', sets initialScrollDone to suppress it —
  // but ONLY when no eligible selection owns the entry scroll (see the guard below).
  useLayoutEffect(() => {
    if (!laneRef.current) return;

    // SELECTION BEATS ANCHOR: when a selected shot can be centered, the selected-shot effect
    // below owns the entry scroll. Reanchoring here consumes initialScrollDone and leaves the
    // shot off-screen — the Board→Script→Timeline bug. The DIRECT Board→Timeline path already
    // behaves this way by accident (the board's unmount anchor write lands after this
    // component's render, so pickScrollTarget sees the pre-write anchor and returns
    // restore/none); this makes every entry path match it. getState() reads, not the render
    // closures: the board's cleanup may have written the anchor in THIS commit.
    const ps = useProjectStore.getState();
    if (selectionOwnsEntryScroll({
          selectedShotId: ps.selectedShotId,
          activeSceneId: ps.activeSceneId,
          sceneIdOfSelectedShot: ps.project?.scenes
            .find(sc => sc.shots.some(sh => sh.id === ps.selectedShotId))?.id ?? null,
        })) return;

    const decision = pickScrollTarget({
      savedAnchor: timelineAnchor,
      savedOffset: scrollLeft,
      currentAnchor: sceneBoardAnchorSceneId,
    });
    if (decision.kind !== 'reanchor') return; // 'restore' and 'none': leave existing behaviour

    // Build SceneTimeSpan[] in absolute timeline time.
    // viewOffset is 0 in all-scenes mode and the scene's global start in single-scene mode.
    const scenes = getActiveScenes();
    const vOffset = getViewOffset();
    let cumTime = vOffset;
    const spans: SceneTimeSpan[] = scenes.map(scene => {
      const start = cumTime;
      cumTime += scene.shots.reduce((acc, s) => acc + s.duration, 0);
      return { sceneId: scene.id, start, end: cumTime };
    });

    const targetSpan = spans.find(s => s.sceneId === decision.sceneId);
    if (!targetSpan) return;

    const pps = pixelsPerSecond; // from render-time subscription
    const container = laneRef.current;
    const newLeft = minimalScrollOffsetToReveal({
      targetStart: timeToScrollLeft(targetSpan.start, pps, vOffset),
      targetEnd:   timeToScrollLeft(targetSpan.end,   pps, vOffset),
      viewportSize:  container.clientWidth,
      currentOffset: container.scrollLeft,
    });

    // Apply synchronously before paint
    container.scrollLeft = newLeft;
    if (rulerRef.current) rulerRef.current.scrollLeft = newLeft;
    setScrollLeft(newLeft); // keeps store + AudioLane in sync

    // Suppress selected-shot centering: with no eligible selection (guard above) the
    // anchor position takes priority — this only reaches an out-of-scope/absent selection.
    initialScrollDone.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — runs once on mount

  // ── Board ⇄ Timeline: capture anchor on unmount ───────────────────────────
  // Empty deps: intentional — only the cleanup function matters (fires on unmount).
  // Reads getState() directly to get values at unmount time, not stale closures.
  // NOTE: reads scrollLeft from the STORE (synced by onLaneScroll) rather than
  // laneRef.current — React detaches host refs before passive-effect cleanups
  // run on unmount, so laneRef.current would be null at this point.
  useEffect(() => {
    return () => {
      // Read current-at-unmount store values (not stale mount-time closures)
      const { pixelsPerSecond: pps, scrollLeft: currentLeft } = useUiStore.getState();
      const vOffset = useProjectStore.getState().getViewOffset();
      const scenes = useProjectStore.getState().getActiveScenes();

      const timeSeconds = scrollLeftToTime(currentLeft, pps, vOffset);

      let cumTime = vOffset;
      const spans: SceneTimeSpan[] = scenes.map(scene => {
        const start = cumTime;
        cumTime += scene.shots.reduce((acc, s) => acc + s.duration, 0);
        return { sceneId: scene.id, start, end: cumTime };
      });

      const anchorId = sceneAtTime(spans, timeSeconds);
      const { setSceneBoardAnchor: setSBA, setTimelineAnchor: setSTA } = useUiStore.getState();
      setSBA(anchorId);
      setSTA(anchorId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — only cleanup runs (on unmount)

  if (!project) return null;

  const viewOffset = getViewOffset();
  const viewDuration = getViewDuration();
  const activeScenes = getActiveScenes();
  const totalWidth = viewDuration * pixelsPerSecond;

  // Auto-scroll to keep playhead visible during playback
  useEffect(() => {
    if (!isPlaying || !laneRef.current) return;
    const container = laneRef.current;
    const playheadX = (currentTime - viewOffset) * pixelsPerSecond;
    const { scrollLeft: sl, clientWidth } = container;

    const margin = clientWidth * 0.2;
    if (playheadX < sl + margin) {
      container.scrollLeft = playheadX - margin;
    } else if (playheadX > sl + clientWidth - margin) {
      container.scrollLeft = playheadX - clientWidth + margin;
    }
  }, [currentTime, isPlaying, pixelsPerSecond, viewOffset]);

  // Sync ruler and store when this lane scrolls (primary scroll source)
  const onLaneScroll = useCallback(() => {
    if (!laneRef.current) return;
    const sl = laneRef.current.scrollLeft;
    if (rulerRef.current) rulerRef.current.scrollLeft = sl;
    isScrollSource.current = true;
    setScrollLeft(sl);
    isScrollSource.current = false;
  }, [setScrollLeft]);

  // Sync this lane when store scrollLeft changes from another source (AudioLane)
  useEffect(() => {
    if (isScrollSource.current) return;
    if (laneRef.current && Math.abs(laneRef.current.scrollLeft - scrollLeft) > 1) {
      laneRef.current.scrollLeft = scrollLeft;
    }
    if (rulerRef.current) rulerRef.current.scrollLeft = scrollLeft;
  }, [scrollLeft]);

  // Scroll to the selected shot on initial load (e.g. bridge selectShot message,
  // or a board selection carried over on board→timeline tab switch)
  useEffect(() => {
    if (initialScrollDone.current || !selectedShotId || !laneRef.current) return;
    initialScrollDone.current = true;
    // Board selection is unconstrained by the Timeline's scene scope: when the
    // Timeline is scoped to a single scene (activeSceneId set via SceneSelector)
    // and the selected shot belongs to a different scene, seek() would clamp the
    // playhead into the scoped scene's window and the Viewer would show the wrong
    // shot — and the scroll math would target a nonsensical position. Skip both.
    // initialScrollDone stays consumed (same give-up-for-this-mount semantics as
    // the startTime < 0 path below). getState() reads avoid stale closures —
    // neither activeSceneId nor project is in this effect's deps.
    const { activeSceneId: scopedSceneId, project: proj } = useProjectStore.getState();
    if (scopedSceneId != null) {
      const shotScene = proj?.scenes.find(sc => sc.shots.some(sh => sh.id === selectedShotId));
      if (shotScene && shotScene.id !== scopedSceneId) return;
    }
    const startTime = getShotStartTime(selectedShotId);
    if (startTime < 0) return;
    // Move the playhead to match — board selection only sets selectedShotId (no seek),
    // so without this the large preview (Viewer, driven by playbackStore.currentTime)
    // keeps showing whatever shot was there before the tab switch. Mirrors the pairing
    // ShotBar's tap handlers already do (selectShot + seek).
    usePlaybackStore.getState().seek(startTime);
    const shotPx = (startTime - getViewOffset()) * pixelsPerSecond;
    const container = laneRef.current;
    const shot = proj?.scenes.flatMap(sc => sc.shots).find(sh => sh.id === selectedShotId);
    const shotEndPx = shotPx + (shot?.duration ?? 0) * pixelsPerSecond;
    // Already fully on screen (e.g. Timeline→Board→Timeline with no selection change):
    // leave the scroll exactly where the user left it. The seek above still runs so the
    // Viewer matches the selection.
    if (isRangeFullyVisible({
          startPx: shotPx, endPx: shotEndPx,
          viewportSize: container.clientWidth, currentOffset: container.scrollLeft,
        })) return;
    // Center the shot in the viewport
    container.scrollLeft = Math.max(0, shotPx - container.clientWidth / 3);
    // Sync ruler and store
    if (rulerRef.current) rulerRef.current.scrollLeft = container.scrollLeft;
    setScrollLeft(container.scrollLeft);
  }, [selectedShotId, pixelsPerSecond, getShotStartTime, getViewOffset, setScrollLeft]);

  const isAnyGenerating = useAiStore(s => s.isAnyGenerating);

  // ── Reorder auto-scroll ────────────────────────────────────────────
  const EDGE_ZONE = 60;
  const SCROLL_SPEED = 400;

  const startReorderAutoScroll = useCallback(() => {
    if (reorderAutoScrollRef.current !== null) return;
    let lastTime = performance.now();
    const tick = (now: number) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      const container = laneRef.current;
      if (!container || !reorderRef.current) { reorderAutoScrollRef.current = null; return; }

      const rect = container.getBoundingClientRect();
      const pointerX = reorderPointerXRef.current;

      const distFromRight = rect.right - pointerX;
      if (distFromRight < EDGE_ZONE) {
        const intensity = 1 - Math.max(0, distFromRight) / EDGE_ZONE;
        container.scrollLeft += SCROLL_SPEED * intensity * dt;
        setScrollLeft(container.scrollLeft);
      }

      const distFromLeft = pointerX - rect.left;
      if (distFromLeft < EDGE_ZONE) {
        const intensity = 1 - Math.max(0, distFromLeft) / EDGE_ZONE;
        container.scrollLeft -= SCROLL_SPEED * intensity * dt;
        setScrollLeft(container.scrollLeft);
      }

      reorderAutoScrollRef.current = requestAnimationFrame(tick);
    };
    reorderAutoScrollRef.current = requestAnimationFrame(tick);
  }, [setScrollLeft]);

  const stopReorderAutoScroll = useCallback(() => {
    if (reorderAutoScrollRef.current !== null) {
      cancelAnimationFrame(reorderAutoScrollRef.current);
      reorderAutoScrollRef.current = null;
    }
  }, []);

  // ── Reorder drag handlers ───────────────────────────────────────────

  /** Get the scene's shots in layout order for reorder calculations */
  const getSceneShots = useCallback((): TimelineShot[] => {
    if (!activeSceneId || !project) return [];
    const scene = project.scenes.find(s => s.id === activeSceneId);
    return scene ? scene.shots : [];
  }, [activeSceneId, project]);

  const onReorderStart = useCallback((e: React.PointerEvent, shotId: number) => {
    if (!activeSceneId || readOnly) return;
    reorderPointerXRef.current = e.clientX;
    reorderRef.current = {
      shotId,
      sceneId: activeSceneId,
      startX: e.clientX,
      activated: false,
      pointerId: e.pointerId,
    };
    // Don't capture yet — capturing steals the pointer from the shot bar
    // and prevents click events. Capture is deferred until the 5px threshold.
  }, [activeSceneId, readOnly]);

  const onReorderMove = useCallback((e: React.PointerEvent) => {
    const r = reorderRef.current;
    if (!r) return;

    reorderPointerXRef.current = e.clientX;

    // 5px threshold before activating
    if (!r.activated) {
      if (Math.abs(e.clientX - r.startX) < 5) return;
      r.activated = true;
      // Now capture so moves outside the bar still track
      laneRef.current?.setPointerCapture(r.pointerId);
      startReorderAutoScroll();
    }

    // Compute target drop index from cursor X vs shot midpoints
    const shots = getSceneShots();
    if (shots.length === 0) return;

    const laneEl = laneRef.current;
    if (!laneEl) return;

    const GAP_INSET = 10;
    const laneRect = laneEl.getBoundingClientRect();
    const cursorX = e.clientX - laneRect.left + laneEl.scrollLeft;

    const draggedIndex = shots.findIndex(s => s.id === r.shotId);

    let cumTime = 0;
    let targetIndex = shots.length; // default: drop at end
    let dropX = 0;

    for (let i = 0; i < shots.length; i++) {
      const shotLeft = cumTime * pixelsPerSecond + GAP_INSET;
      const shotWidth = Math.max(shots[i].duration * pixelsPerSecond - GAP_INSET * 2, 20);
      const mid = shotLeft + shotWidth / 2;

      if (cursorX < mid) {
        targetIndex = i;
        dropX = shotLeft - GAP_INSET;
        break;
      }
      cumTime += shots[i].duration;

      if (i === shots.length - 1) {
        dropX = (cumTime * pixelsPerSecond);
      }
    }

    // Hide indicator at no-op positions: the slot before and after the
    // dragged shot both resolve to the same position after removal.
    if (targetIndex === draggedIndex || targetIndex === draggedIndex + 1) {
      setReorderDropIndex(null);
      return;
    }

    setReorderDropIndex(targetIndex);
    setReorderDropX(dropX);
  }, [getSceneShots, pixelsPerSecond, startReorderAutoScroll]);

  const onReorderEnd = useCallback((e: React.PointerEvent) => {
    stopReorderAutoScroll();
    const r = reorderRef.current;
    if (!r) return;
    reorderRef.current = null;

    laneRef.current?.releasePointerCapture(e.pointerId);

    if (!r.activated || reorderDropIndex === null) {
      setReorderDropIndex(null);
      return;
    }

    // Find old index of the dragged shot
    const shots = getSceneShots();
    const oldIndex = shots.findIndex(s => s.id === r.shotId);

    // Adjust target: if dragging rightward, the removal shifts indices
    let adjustedTarget = reorderDropIndex;
    if (oldIndex >= 0 && oldIndex < adjustedTarget) {
      adjustedTarget--;
    }

    if (oldIndex >= 0 && oldIndex !== adjustedTarget) {
      reorderShotInScene(r.sceneId, r.shotId, adjustedTarget);
    }

    setReorderDropIndex(null);
  }, [getSceneShots, reorderDropIndex, reorderShotInScene, stopReorderAutoScroll]);

  // Click on ruler to seek
  const onRulerClick = useCallback((e: React.MouseEvent) => {
    if (isAnyGenerating()) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const sl = rulerRef.current?.scrollLeft ?? 0;
    const x = e.clientX - rect.left + sl;
    seek(x / pixelsPerSecond + viewOffset);
  }, [pixelsPerSecond, seek, viewOffset, isAnyGenerating]);

  // Close popover on click outside
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!popover) return;
    const handleClick = (e: MouseEvent) => {
      if (popover && popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [popover]);

  const handleDiamondClick = useCallback((
    e: React.MouseEvent,
    fromShotId: number,
    toShotId: number,
  ) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopover({ fromShotId, toShotId, x: rect.left + rect.width / 2, y: rect.top });
  }, []);

  const getAssetsForTransition = useAiStore(s => s.getAssetsForTransition);
  const removeTransitionVideo = useProjectStore(s => s.removeTransitionVideo);

  const handlePopoverSelect = useCallback((type: TransitionType) => {
    if (!popover) return;
    if (type === 'continuous') {
      // Set transition and open AI workspace pre-scoped to this pair
      setTransition(popover.fromShotId, popover.toShotId, 'continuous');
      const pair = { fromShotId: popover.fromShotId, toShotId: popover.toShotId };
      setPopover(null);
      setTimeout(() => {
        // The workspace's landing tab became 'create' on 2026-08-25 and only the Enhance tab
        // hosts the First/Last Shot Video task — name it rather than inherit the default.
        // Written BEFORE the open (T1+T2, 2026-09-01): the flow's open effect reads
        // aiWorkspaceTab to decide whether to seed the playhead shot, and a 'create' landing
        // selects nothing — so the tab must be committed before the workspace mounts.
        useUiStore.getState().setAiWorkspaceTab('stylegrade');
        openAiWorkspace('continuous', pair);
      }, 0);
      return;
    }
    // Safeguard: if changing FROM continuous, check for existing transition video
    const current = getTransition(popover.fromShotId, popover.toShotId);
    if (current?.type === 'continuous' && current.videoObjectUrl) {
      const assets = getAssetsForTransition(current.id);
      const msg = assets.credits > 0
        ? t('changeTransitionTypeWithCredits', { credits: assets.credits })
        : t('changeTransitionType');
      if (!window.confirm(msg)) {
        setPopover(null);
        return;
      }
      removeTransitionVideo(popover.fromShotId, popover.toShotId);
    }
    setTransition(popover.fromShotId, popover.toShotId, type);
    setPopover(null);
  }, [popover, setTransition, getTransition, getAssetsForTransition, removeTransitionVideo, openAiWorkspace, t]);

  // Build scene dividers + shot bars + transition diamonds
  const shotBars: React.ReactNode[] = [];
  const sceneDividers: React.ReactNode[] = [];
  const diamonds: React.ReactNode[] = [];
  let cumTime = 0;

  const allShots = activeScenes.flatMap(s => s.shots);

  // Viewport windowing: only render elements in the visible scroll range (+ buffer)
  const VIRTUALIZE_THRESHOLD = 100;
  const BUFFER_PX = 300;
  const shouldVirtualize = allShots.length > VIRTUALIZE_THRESHOLD;
  const visibleLeft = shouldVirtualize ? scrollLeft - BUFFER_PX : -Infinity;
  const visibleRight = shouldVirtualize ? scrollLeft + containerWidthRef.current + BUFFER_PX : Infinity;

  for (const scene of activeScenes) {
    const sceneStart = cumTime;

    const sceneStartPx = sceneStart * pixelsPerSecond;
    if (sceneStartPx >= visibleLeft && sceneStartPx <= visibleRight) {
      // filter(Boolean)-joined so an empty scene name (no location set) doesn't leave a stray
      // trailing "1. " in the timeline's scene label strip.
      const sceneLabelText = [scene.number, scene.name].filter(Boolean).join('. ');
      sceneDividers.push(
        <div key={`div-${scene.id}`} className={styles.sceneDivider} style={{ left: sceneStartPx }} />,
        <div
          key={`label-${scene.id}`}
          className={styles.sceneLabel}
          style={{ left: sceneStartPx + 4, background: scene.color }}
        >
          {sceneLabelText}
        </div>
      );
    }

    for (const shot of scene.shots) {
      const shotLeftPx = cumTime * pixelsPerSecond;
      const shotRightPx = (cumTime + shot.duration) * pixelsPerSecond;
      if (shotRightPx >= visibleLeft && shotLeftPx <= visibleRight) {
        shotBars.push(
          <ShotBar
            key={shot.id}
            shot={shot}
            sceneColor={scene.color}
            startTime={cumTime}
            pixelsPerSecond={pixelsPerSecond}
            onReorderStart={activeSceneId !== null ? onReorderStart : undefined}
            laneContainerRef={laneRef}
          />
        );
      }

      cumTime += shot.duration;

      // Diamond at boundary between consecutive shots
      const shotIdx = allShots.indexOf(shot);
      const diamondPx = cumTime * pixelsPerSecond;
      if (shotIdx >= 0 && shotIdx < allShots.length - 1 && diamondPx >= visibleLeft && diamondPx <= visibleRight) {
        const nextShot = allShots[shotIdx + 1];
        const trans = getTransition(shot.id, nextShot.id);
        const transType = trans?.type ?? 'cut';
        const isActive = transType !== 'cut';
        const hasVideo = transType === 'continuous' && !!trans?.videoObjectUrl;
        const letter = transType === 'dissolve' ? 'D'
          : transType === 'fade_to_black' ? 'F'
          : transType === 'continuous' ? '\u27F7'
          : 'C';

        diamonds.push(
          <button
            key={`diamond-${shot.id}-${nextShot.id}`}
            className={`${styles.diamond} ${isActive ? styles.diamondActive : ''} ${hasVideo ? styles.diamondVideo : ''}`}
            style={{ left: diamondPx }}
            onClick={readOnly ? undefined : (e) => handleDiamondClick(e, shot.id, nextShot.id)}
            title={readOnly ? t('transitionReadOnlyTitle', { type: transType }) : t('clickToSetTransition')}
          >
            <span className={styles.diamondLetter}>{letter}</span>
          </button>
        );
      }
    }
  }

  // Ruler ticks — labels show absolute time (viewOffset-based)
  const rulerTicks: React.ReactNode[] = [];
  const tickInterval = pixelsPerSecond >= 60 ? 1 : pixelsPerSecond >= 30 ? 5 : 10;
  const tickStart = shouldVirtualize
    ? Math.max(0, Math.floor((scrollLeft - BUFFER_PX) / pixelsPerSecond / tickInterval) * tickInterval)
    : 0;
  const tickEnd = shouldVirtualize
    ? Math.min(viewDuration, Math.ceil((scrollLeft + containerWidthRef.current + BUFFER_PX) / pixelsPerSecond / tickInterval) * tickInterval)
    : viewDuration;
  for (let t = tickStart; t <= tickEnd; t += tickInterval) {
    const x = t * pixelsPerSecond;
    const absTime = t + viewOffset;
    const isMajor = t % (tickInterval * 5 === 0 ? tickInterval : tickInterval) === 0;
    rulerTicks.push(
      <div
        key={`tick-${t}`}
        className={`${styles.rulerTick} ${isMajor ? styles.rulerTickMajor : styles.rulerTickMinor}`}
        style={{ left: x }}
      />
    );
    if (isMajor || tickInterval <= 1) {
      const mins = Math.floor(absTime / 60);
      const secs = Math.floor(absTime % 60);
      rulerTicks.push(
        <div key={`label-${t}`} className={styles.rulerLabel} style={{ left: x }}>
          {mins}:{secs.toString().padStart(2, '0')}
        </div>
      );
    }
  }

  return (
    <>
      {/* Time ruler */}
      <div className={styles.ruler} ref={rulerRef} onClick={onRulerClick} style={{ overflowX: 'hidden' }}>
        <div className={styles.rulerInner} style={{ width: totalWidth }}>
          {rulerTicks}
          <Playhead height={isMobile ? 12 : 24} />
        </div>
      </div>

      {/* Shot lane */}
      <div
        className={styles.laneContainer}
        ref={laneRef}
        onScroll={onLaneScroll}
        onPointerMove={onReorderMove}
        onPointerUp={onReorderEnd}
        onPointerCancel={onReorderEnd}
      >
        <div className={styles.laneInner} style={{ width: totalWidth }}>
          {sceneDividers}
          {shotBars}
          {diamonds}
          {reorderDropIndex !== null && (
            <div className={styles.reorderIndicator} style={{ left: reorderDropX }} />
          )}
          <Playhead height={isMobile ? 40 : 80} />
        </div>
      </div>

      {/* Transition popover */}
      {popover && (
        <div
          ref={popoverRef}
          className={styles.popover}
          style={{ left: popover.x, top: popover.y }}
        >
          {([
            ['cut', '\u2014', t('transitionLabelCut')] as const,
            ['dissolve', '\u2715', t('transitionLabelDissolve')] as const,
            ['fade_to_black', '\u25A0', t('transitionLabelFade')] as const,
          ]).map(([type, icon, label]) => {
            const trans = getTransition(popover.fromShotId, popover.toShotId);
            const currentType = trans?.type ?? 'cut';
            const isSelected = currentType === type;
            return (
              <button
                key={type}
                className={`${styles.popoverBtn} ${isSelected ? styles.popoverBtnActive : ''}`}
                onClick={() => handlePopoverSelect(type)}
                title={label}
              >
                <span className={styles.popoverIcon}>{icon}</span>
                <span className={styles.popoverLabel}>{label}</span>
              </button>
            );
          })}
        </div>
      )}

    </>
  );
}

